import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import useWindowDimensions from 'hooks/useWindowDimensions';
import tablet from 'utils/Dimensions';
import FontIcon from 'components/FontIcon';
import { getPackagePricing, showOriginalPricing } from 'utils/Pricing';
import { priceFormatter } from 'utils/Numbers';
import ExpandableList from 'components/ExpandableList';
import PackageTabCModal from '../PackageTabCModal/PackageTabCModal';
import PackageTabCContent from '../PackageTabCContent/PackageTabCContent';
import classes from './PackageTabCTab.module.scss';

const {
  string,
  array,
  number,
  func,
} = PropTypes;

const PackageTabCTab = (props) => {
  const {
    leftCopy,
    rightCopy,
    topCopy,
    bottomCopy,
    expandableItems = [],
    expandableItemsLocation,
    expandableItemsHeader,
    icon,
    packageGridTitle,
    heading,
    products = [],
    packageAttributes = [],
    featuredProduct,
    selectedIndex,
    handleClick,
    index,
    categories,
    expandText,
    collapseText,
    image,
  } = props;

  const locations = {
    top: 'Top',
    left: 'Left',
    right: 'Right',
    bottom: 'Bottom',
    attributes: 'Attributes',
  };

  const isActive = selectedIndex === index;

  const ref = useRef(null);
  const firstUpdate = useRef(true);
  const contentRef = useRef(null);
  const { width } = useWindowDimensions();

  useEffect(() => {
    function scroll() {
      if (firstUpdate.current !== null && ref.current !== null && isActive) {
        ref.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });

        if (contentRef.current !== null) {
          contentRef.current.classList.add(classes.heightAuto);
        }
      }

      if (firstUpdate.current) {
        firstUpdate.current = false;
      }
    }

    if (contentRef.current !== null) {
      if (isActive) {
        const addHeight = width < tablet ? 40 : 80;
        contentRef.current.style.height = `
          ${contentRef.current.scrollHeight
            + (contentRef.current.clientHeight < contentRef.current.scrollHeight ? addHeight : 0)}px
        `;
      } else {
        if (contentRef.current.classList.contains(classes.heightAuto)) {
          contentRef.current.classList.remove(classes.heightAuto);
        }
        contentRef.current.style.height = 0;
      }
    }

    window.addEventListener('load', scroll);
    return () => window.removeEventListener('load', scroll);
  }, [isActive]); // eslint-disable-line react-hooks/exhaustive-deps

  const onTransitionEnd = () => {
    const { current } = contentRef;
    if (current !== null) {
      const { classList } = current;
      if (isActive && !classList.contains(classes.heightAuto)) {
        classList.add(classes.heightAuto);
      } else if (!isActive && classList.contains(classes.heightAuto)) {
        classList.remove(classes.heightAuto);
      }
    }
  };

  function renderContentSection(copy, location) {
    return (
      (copy || (location === expandableItemsLocation && expandableItems.length > 0)) &&
        <div className={classes.section}>
          {copy && typeof copy === 'string'
            ? <div
              className={classes.contentContainer}
              dangerouslySetInnerHTML={{ __html: copy }}
            />
            : copy &&
              <div className={classes.contentContainer}>
                {copy}
              </div>}
          {location === expandableItemsLocation && expandableItemsHeader &&
            <PackageTabCModal header={expandableItemsHeader}>
              <ExpandableList type='ExpandableListItemD' items={expandableItems} />
            </PackageTabCModal>}
        </div>
    );
  }

  const {
    uniquePrices,
    lowestPriceLowestOriginalPrice,
  } = getPackagePricing(products);

  return (
    <div
      className={classes.container}
      ref={ref}
    >
      <button
        type='button'
        aria-label={`${index}-expand-collapse`}
        onClick={() => {
          handleClick(isActive ? -1 : index);
        }}
        className={classNames(classes.button, !isActive && classes.rounded)}
      >
        <div className={classes.top}>
          {featuredProduct &&
            <div className={classes.attention}>
              {featuredProduct.toUpperCase()}
              <span className={classes.background} />
            </div>}
          <div className={classes.column}>
            <div className={classes.section}>
              {icon &&
                <div className={classes.iconContainer}>
                  <FontIcon icon={icon} />
                </div>}
              {packageGridTitle &&
                <h3
                  className={classes.packageGridTitle}
                  dangerouslySetInnerHTML={{ __html: packageGridTitle }}
                />}
              {products.length > 0 &&
                <div className={classes.packagePricing}>
                  {uniquePrices.length > 1 &&
                    'Starting at '}
                  {lowestPriceLowestOriginalPrice.price >= 0
                  && priceFormatter(lowestPriceLowestOriginalPrice.price)}
                  {showOriginalPricing(lowestPriceLowestOriginalPrice) &&
                    <span className={classes.originalPackagePricing}>
                      {priceFormatter(lowestPriceLowestOriginalPrice.originalPrice)}
                    </span>}
                </div>}
            </div>
            {renderContentSection(
              leftCopy,
              locations.left,
            )}
          </div>
          <div className={classes.column}>
            {renderContentSection(
              rightCopy,
              locations.right,
            )}
          </div>
        </div>
        <div className={isActive ? classes.dividerOpen : classes.dividerClosed}>
          {isActive ? collapseText : expandText}
          <FontIcon icon='far fa-angle-down' className={classes.expandCollapseIcon} />
        </div>
      </button>
      <PackageTabCContent
        ref={contentRef}
        onTransitionEnd={onTransitionEnd}
        isActive={isActive}
        heading={heading}
        topCopy={topCopy}
        bottomCopy={bottomCopy}
        packageAttributes={packageAttributes}
        categories={categories}
        renderContentSection={renderContentSection}
        expandableItemsHeader={expandableItemsHeader}
        expandableItems={expandableItems}
        expandableItemsLocation={expandableItemsLocation}
        locations={locations}
        products={products}
        image={image}
      />
    </div>
  );
};

PackageTabCTab.propTypes = {
  index: number,
  heading: string,
  topCopy: string,
  rightCopy: string,
  leftCopy: string,
  bottomCopy: string,
  products: array,
  expandableItems: array,
  expandableItemsLocation: string,
  expandableItemsHeader: string,
  icon: string,
  packageGridTitle: string,
  featuredProduct: string,
  categories: array,
  expandText: string,
  collapseText: string,
  selectedIndex: number,
  handleClick: func,
  packageAttributes: array,
  image: PropTypes.shape({
    imageURI: PropTypes.string,
    imageAltText: PropTypes.string,
  }),
};

export default PackageTabCTab;
